import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import type { UiHelpItem } from "@shared/schema";

interface PageHelpContextValue {
  pageId: string;
  helpEnabled: boolean;
  toggleHelp: () => void;
  helpItems: UiHelpItem[];
  getHelpItem: (elementKey: string) => UiHelpItem | undefined;
}

const PageHelpContext = createContext<PageHelpContextValue>({
  pageId: "",
  helpEnabled: false,
  toggleHelp: () => {},
  helpItems: [],
  getHelpItem: () => undefined,
});

interface PageHelpProviderProps {
  pageId: string;
  children: React.ReactNode;
}

export function PageHelpProvider({ pageId, children }: PageHelpProviderProps) {
  const storageKey = `help_mode_${pageId}`;
  const [helpEnabled, setHelpEnabled] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      setHelpEnabled(localStorage.getItem(storageKey) === "true");
    } catch {
      setHelpEnabled(false);
    }
  }, [storageKey]);

  const { data: helpItems = [] } = useQuery<UiHelpItem[]>({
    queryKey: ["/api/help-items", pageId],
    queryFn: async () => {
      const res = await fetch(`/api/help-items?pageId=${encodeURIComponent(pageId)}`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: true,
  });

  const toggleHelp = useCallback(() => {
    setHelpEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(storageKey, String(next));
      } catch {}
      return next;
    });
  }, [storageKey]);

  const getHelpItem = useCallback(
    (elementKey: string) => helpItems.find((item) => item.elementKey === elementKey && item.isActive),
    [helpItems]
  );

  return (
    <PageHelpContext.Provider value={{ pageId, helpEnabled, toggleHelp, helpItems, getHelpItem }}>
      {children}
    </PageHelpContext.Provider>
  );
}

export function usePageHelp() {
  return useContext(PageHelpContext);
}
